// src/components/StudentDashboard.js

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function StudentDashboard() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [activeCourse, setActiveCourse] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [backendUrl, setBackendUrl] = useState("");

  const userId = localStorage.getItem("userId");

  // -------------------------------------------------------------
  // LOAD BACKEND URL FROM config.js
  // -------------------------------------------------------------
  useEffect(() => {
    const script = document.createElement("script");

    script.src = "/config.js";

    script.onload = () => {
      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      } else {
        console.error("❌ BACKEND_URL not found in config.js");
      }
    };

    script.onerror = () => {
      console.error("❌ Failed to load config.js");
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // -------------------------------------------------------------
  // FETCH ENROLLED COURSES + EXAM ELIGIBILITY
  // -------------------------------------------------------------
  const fetchCourses = async () => {
    if (!backendUrl || !userId) return;

    try {
      const res = await axios.get(
        `${backendUrl}/student-enrolled-courses/${userId}`
      );

      const enrolled = res.data || [];

      const updated = await Promise.all(
        enrolled.map(async (course) => {
          const courseId = Number(course.Course_ID);

          let eligibility = {
            allowed: false,
            exam: null,
            reason: null,
          };

          try {
            const statusRes = await axios.get(
              `${backendUrl}/student-exam-status/${courseId}/${userId}`
            );

            eligibility = statusRes.data || eligibility;

            console.log(
              "PROD EXAM STATUS:",
              course.Course_Name,
              eligibility
            );
          } catch (examErr) {
            console.error(
              `Error fetching exam status for course ${courseId}:`,
              examErr
            );
          }

          const exam = eligibility.exam;

          let canAttempt = false;

          if (exam) {
            const requireMaterial =
              exam.materialCompletionRequired === true ||
              exam.materialCompletionRequired === 1;

            // Material completion required
            if (requireMaterial) {
              canAttempt =
                course.CompletionStatus === "Completed";
            }

            // Material completion NOT required
            else {
              canAttempt = true;
            }
          }

          return {
            ...course,

            Course_ID: courseId,

            progress: course.ProgressPercent,

            examDetails: exam,

            // Backend controls whether exam is released
            examUnlocked: eligibility.allowed,

            // Student must also satisfy material requirement
            canAttemptExam: canAttempt,

            // Show exam button whenever an exam exists
            showAttemptButton: !!exam,

            score: exam?.Score ?? null,

            reason: eligibility.reason ?? null,
          };
        })
      );

      setCourses(updated);
    } catch (err) {
      console.error("❌ Error fetching courses:", err);
    }
  };

  useEffect(() => {
    if (backendUrl && userId) {
      fetchCourses();
    }
  }, [backendUrl, userId]);

  // -------------------------------------------------------------
  // VIEW MY EXAM RESULTS
  // -------------------------------------------------------------
  const handleViewResults = () => {
    navigate("/student/results");
  };

  // -------------------------------------------------------------
  // MATERIAL VIEW LOGIC
  // -------------------------------------------------------------
  const handleViewMaterials = async (courseId) => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/student/${userId}/uploads`
      );

      const filtered =
        res.data?.filter(
          (m) => Number(m.Course_ID) === Number(courseId)
        ) || [];

      setMaterials(filtered);
      setActiveCourse(courseId);
    } catch (err) {
      console.error("❌ Failed fetching materials:", err);
    }
  };

  // -------------------------------------------------------------
  // MATERIAL CLICK HANDLER
  // -------------------------------------------------------------
  const handleMaterialClick = async (material, courseId) => {
    try {
      await axios.post(
        `${backendUrl}/student/view-material`,
        {
          studentId: userId,
          uploadId: material.Upload_ID,
          courseId,
        }
      );

      await fetchCourses();

      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error(
        "❌ Failed logging material click:",
        err
      );
    }

    let url = "";

    if (
      material.FileType &&
      material.FileType.startsWith("video/")
    ) {
      url =
        `/watch.html?file=${encodeURIComponent(
          material.FileName
        )}` +
        `&uploadId=${material.Upload_ID}` +
        `&courseId=${courseId}` +
        `&userId=${userId}`;
    } else if (
      material.FileName
        .toLowerCase()
        .endsWith(".ppt") ||
      material.FileName
        .toLowerCase()
        .endsWith(".pptx")
    ) {
      url =
        `/pptviewer.html?file=${encodeURIComponent(
          material.FileName
        )}` +
        `&uploadId=${material.Upload_ID}` +
        `&courseId=${courseId}` +
        `&userId=${userId}`;
    } else {
      url =
        `/viewer.html?file=${encodeURIComponent(
          material.FileName
        )}` +
        `&uploadId=${material.Upload_ID}` +
        `&courseId=${courseId}` +
        `&userId=${userId}`;
    }

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // -------------------------------------------------------------
  // START EXAM BUTTON HANDLER
  // -------------------------------------------------------------
  const handleAttemptExam = (courseId) => {
    const selected = courses.find(
      (c) => c.Course_ID === courseId
    );

    const examId =
      selected?.examDetails?.Exam_ID;

    if (!examId) {
      alert("⚠️ Exam not available.");
      return;
    }

    if (!selected.examUnlocked) {
      alert(
        "⚠️ Exam is locked or not released yet."
      );
      return;
    }

    if (!selected.canAttemptExam) {
      alert(
        "⚠️ Please complete all materials to unlock this exam."
      );
      return;
    }

    window.location.href =
      `/student-exam/${examId}/${userId}`;
  };

  // -------------------------------------------------------------
  // RENDER UI
  // -------------------------------------------------------------
  return (
    <div style={{ padding: 5 }}>

      {/* =========================================================
          MY EXAM RESULTS
          ========================================================= */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 20,
        }}
      >
        <button
          onClick={handleViewResults}
          style={resultsButton}
        >
          📊 My Exam Results
        </button>
      </div>

      {/* =========================================================
          ENROLLED COURSES
          ========================================================= */}
      <h2>📚 Enrolled Training Modules</h2>

      {courses.length === 0 ? (
        <p>No enrolled courses.</p>
      ) : (
        <div
          style={{
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          {courses.map((course) => (
            <div
              key={`${course.Course_ID}-${refreshKey}`}
              style={cardStyle}
            >
              <h3>{course.Course_Name}</h3>

              {/* -------------------------------------------------
                  PROGRESS CIRCLE
                  ------------------------------------------------- */}
              <div style={progressWrapper}>
                <svg
                  viewBox="0 0 36 36"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <path
                    style={{
                      fill: "none",
                      stroke: "#eee",
                      strokeWidth: 3.8,
                    }}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />

                  <path
                    style={{
                      fill: "none",
                      stroke: "#4caf50",
                      strokeWidth: 3.8,
                      strokeLinecap: "round",
                    }}
                    strokeDasharray={`${course.progress}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />

                  <text
                    x="18"
                    y="20.35"
                    fill="#000"
                    fontSize="7"
                    textAnchor="middle"
                  >
                    {course.progress}%
                  </text>
                </svg>
              </div>

              {/* -------------------------------------------------
                  VIEW MATERIALS
                  ------------------------------------------------- */}
              <button
                onClick={() =>
                  handleViewMaterials(
                    course.Course_ID
                  )
                }
                style={materialButton}
              >
                📂 View Materials
              </button>

              {/* -------------------------------------------------
                  EXAM BUTTON
                  ------------------------------------------------- */}
              {course.showAttemptButton && (
                <button
                  onClick={() =>
                    handleAttemptExam(
                      course.Course_ID
                    )
                  }
                  style={{
                    ...materialButton,
                    marginTop: 10,

                    backgroundColor:
                      !course.examUnlocked
                        ? "#808080"
                        : !course.canAttemptExam
                        ? "#FFA500"
                        : "#E30016",

                    cursor:
                      course.examUnlocked &&
                      course.canAttemptExam
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  {!course.examUnlocked
                    ? "Locked"
                    : !course.canAttemptExam
                    ? "Complete Materials"
                    : "Attempt Exam"}
                </button>
              )}

              {/* -------------------------------------------------
                  LAST SCORE
                  ------------------------------------------------- */}
              {course.score !== null && (
                <p
                  style={{
                    marginTop: 10,
                    fontWeight: 500,
                  }}
                >
                  Score: <b>{course.score}</b>
                </p>
              )}

              {/* -------------------------------------------------
                  MATERIAL LIST
                  ------------------------------------------------- */}
              {activeCourse ===
                course.Course_ID && (
                <ul style={materialsList}>
                  {materials.length === 0 ? (
                    <li
                      style={{
                        color: "#777",
                      }}
                    >
                      ⚠️ No materials released.
                    </li>
                  ) : (
                    materials.map((m) => (
                      <li key={m.Upload_ID}>
                        <button
                          onClick={() =>
                            handleMaterialClick(
                              m,
                              course.Course_ID
                            )
                          }
                          style={
                            materialItemButton
                          }
                        >
                          {m.FileType &&
                          m.FileType.startsWith(
                            "video/"
                          )
                            ? "▶️"
                            : "📄"}{" "}
                          {m.FileName}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =============================================================
   STYLES
   ============================================================= */

const cardStyle = {
  width: 250,
  padding: 20,
  borderRadius: 12,
  backgroundColor: "#fff",
  boxShadow:
    "0 4px 12px rgba(0,0,0,0.1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const progressWrapper = {
  position: "relative",
  width: 100,
  height: 100,
  marginBottom: 15,
};

const resultsButton = {
  backgroundColor: "#C70013",
  color: "#fff",
  border: "none",
  padding: "10px 18px",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: 600,
};

const materialButton = {
  backgroundColor: "#8b8d8e",
  color: "#fff",
  border: "none",
  padding: "8px 16px",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
};

const materialsList = {
  listStyle: "none",
  padding: 0,
  marginTop: 10,
  width: "100%",
};

const materialItemButton = {
  background: "none",
  border: "none",
  color: "#E30016",
  cursor: "pointer",
  fontWeight: 500,
};